import Splide from '@splidejs/splide';

let pageSplide = null;
let lastNavigationWasKeyboard = false;
let pageSliderBound = false;
let lastSlideChangeAtMs = 0;

const minimumSlideVisibleMs = 1200;

const activeSectionChangeEventName = 'section-keyboard:active-section-change';
const sectionsReadyClassName = 'sections-ready';
const desktopWidthMediaQuery = window.matchMedia('(min-width: 900px)');
const landscapeMediaQuery = window.matchMedia('(orientation: landscape)');

function isEditableElement(target) {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
}

function getActiveSectionElement() {
  if (!pageSplide) {
    return null;
  }

  const activeSlide = pageSplide.Components.Slides.getAt(pageSplide.index);

  if (!activeSlide || !activeSlide.slide) {
    return null;
  }

  return activeSlide.slide.querySelector('section');
}

function notifyActiveSectionChange() {
  document.dispatchEvent(new CustomEvent(activeSectionChangeEventName, {
    detail: {
      section: getActiveSectionElement(),
      viaKeyboard: lastNavigationWasKeyboard,
    },
  }));

  lastNavigationWasKeyboard = false;
}

function isSliderInteractionReady() {
  return document.documentElement.classList.contains(sectionsReadyClassName);
}

function blockSliderInteractionBeforeReady(event) {
  if (isSliderInteractionReady()) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
}

function updateSlideChangeTimestamp() {
  lastSlideChangeAtMs = Date.now();
}

function blockSliderWheelUntilVisibleTime(event) {
  if (!isSliderInteractionReady() || document.body.classList.contains('teaser-modal-open')) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  if (Date.now() - lastSlideChangeAtMs >= minimumSlideVisibleMs) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function handleVerticalKeyboard(event) {
  if (!pageSplide || !isSliderInteractionReady() || isEditableElement(event.target) || document.body.classList.contains('teaser-modal-open')) {
    return;
  }

  if (event.key === 'ArrowUp' || event.key === 'PageUp') {
    event.preventDefault();
    lastNavigationWasKeyboard = true;
    pageSplide.go('<');
    return;
  }

  if (event.key === 'ArrowDown' || event.key === 'PageDown') {
    event.preventDefault();
    lastNavigationWasKeyboard = true;
    pageSplide.go('>');
  }
}

function shouldUseSlider() {
  const wideEnough = desktopWidthMediaQuery.matches;
  const landscape = landscapeMediaQuery.matches;

  return wideEnough && landscape;
}

function getSlideIndexBySectionName(sectionName) {
  if (!pageSplide || typeof sectionName !== 'string' || sectionName.trim() === '') {
    return -1;
  }

  const slides = Array.from(document.querySelectorAll('#page-slider .splide__slide[data-page-section]'));

  return slides.findIndex((slide) => slide.dataset.pageSection === sectionName);
}

function mountPageSlider() {
  if (pageSplide) {
    return;
  }

  pageSplide = new Splide('#page-slider', {
    direction: 'ttb',
    type: 'slide',
    height: '100vh',
    wheel: true,
    wheelMinThreshold: 0,
    wheelSleep: 1000,
    drag: true,
    snap: true,
    keyboard: false,
    pagination: true,
    arrows: false,
    speed: 750,
    easing: 'cubic-bezier(0.77, 0, 0.175, 1)',
  });

  pageSplide.on('mounted moved', () => {
    updateSlideChangeTimestamp();
    notifyActiveSectionChange();
  });
  pageSplide.mount();
  document.addEventListener('keydown', handleVerticalKeyboard);

  updateSlideChangeTimestamp();

  const track = pageSplide.root.querySelector('.splide__track');

  if (track) {
    track.addEventListener('wheel', blockSliderInteractionBeforeReady, {
      passive: false,
      capture: true,
    });
    document.addEventListener('wheel', blockSliderWheelUntilVisibleTime, {
      passive: false,
      capture: true,
    });
    track.addEventListener('pointerdown', blockSliderInteractionBeforeReady, true);
    track.addEventListener('mousedown', blockSliderInteractionBeforeReady, true);
    track.addEventListener('touchstart', blockSliderInteractionBeforeReady, {
      passive: false,
      capture: true,
    });
  }
}

function destroyPageSlider() {
  if (!pageSplide) {
    return;
  }

  document.removeEventListener('keydown', handleVerticalKeyboard);
  document.removeEventListener('wheel', blockSliderWheelUntilVisibleTime, {
    capture: true,
  });
  pageSplide.destroy(true);
  pageSplide = null;
  lastNavigationWasKeyboard = false;
  lastSlideChangeAtMs = 0;

  document.dispatchEvent(new CustomEvent(activeSectionChangeEventName, {
    detail: {
      section: null,
      viaKeyboard: false,
    },
  }));
}

export function syncPageSlider() {
  if (shouldUseSlider()) {
    mountPageSlider();
    return;
  }

  destroyPageSlider();
}

export function initPageSlider() {
  if (pageSliderBound) {
    return;
  }

  const handleMediaChange = () => {
    syncPageSlider();
  };

  desktopWidthMediaQuery.addEventListener('change', handleMediaChange);
  landscapeMediaQuery.addEventListener('change', handleMediaChange);
  pageSliderBound = true;
  syncPageSlider();
}

export function isPageSliderActive() {
  return Boolean(pageSplide);
}

export function goToPageSection(sectionName) {
  const index = getSlideIndexBySectionName(sectionName);

  if (index < 0 || !pageSplide) {
    return false;
  }

  pageSplide.go(index);
  return true;
}
